/**
 * MeetingTaskCandidates — 会议待办的确认界面。
 *
 * AI 从会议内容识别出的待办先落成候选条目，不进任务列表；律师在这里逐条决定保留
 * 或丢弃、指派给谁、定什么截止时间，确认之后才成为正式任务。
 *
 * 技能识别到的责任人与截止说法是自然语言（「下周五之前」这类），只作为参考原文展示
 * 在条目下方，不预填进指派人下拉框与日期控件——预填等于把一个未经确认的推断写成
 * 事实。指派人必填，截止时间可留空。
 */

import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useUsers } from "../../api/queries";
import type { MeetingTaskCandidate, MeetingTaskCandidateDecision } from "../../types/meeting";
import EmptyState from "../common/EmptyState";
import { SkeletonRow } from "../common/Skeleton";

const { Text, Paragraph } = Typography;

/** 候选条目的优先级取值，与任务中心一致。 */
const PRIORITY_OPTIONS = [
  { value: "urgent", label: "紧急" },
  { value: "high", label: "重要" },
  { value: "normal", label: "常规" },
  { value: "low", label: "暂缓" },
];

const PRIORITY_TAG_COLOR: Record<string, string> = {
  urgent: "red",
  high: "orange",
  normal: "blue",
  low: "default",
};

/** 一条候选在界面上的可编辑状态。 */
interface CandidateDraft {
  keep: boolean;
  assigneeId: string | undefined;
  deadline: Dayjs | null;
  priority: string;
}

function initialDraft(candidate: MeetingTaskCandidate): CandidateDraft {
  return {
    keep: true,
    // 指派人不预填：技能给出的是一个姓名字符串，不是系统用户。
    assigneeId: undefined,
    // 截止时间不预填：技能给出的是自然语言说法，原文在下方作参考。
    deadline: null,
    priority: candidate.priority || "normal",
  };
}

interface MeetingTaskCandidatesProps {
  meetingId: string;
  candidates: MeetingTaskCandidate[] | undefined;
  isLoading: boolean;
  /** 确认提交是否在途。 */
  submitting: boolean;
  /** 上一次确认失败的原因；为 null 时不显示。 */
  submitError: string | null;
  onConfirm: (decisions: MeetingTaskCandidateDecision[]) => void;
  /** 尚未抽取过待办时展示的引导语。 */
  emptyHint?: string;
  /**
   * 纪要是否已经过本人确认（S11 确认门禁）。为 false 时确认按钮禁用，并给出
   * 「请先确认纪要」提示——候选待办来自未确认的 AI 草稿，确认前不允许它们
   * 变成正式任务。默认 true，兼容尚未接入确认门禁的调用方。
   */
  confirmed?: boolean;
}

export default function MeetingTaskCandidates({
  meetingId,
  candidates,
  isLoading,
  submitting,
  submitError,
  onConfirm,
  emptyHint,
  confirmed = true,
}: MeetingTaskCandidatesProps) {
  const [drafts, setDrafts] = useState<Record<string, CandidateDraft>>({});
  const usersQuery = useUsers({ active: true, pageSize: 200 });

  const assigneeOptions = useMemo(
    () =>
      (usersQuery.data?.data ?? []).map((u) => ({
        value: u.id,
        label: u.name || u.email,
      })),
    [usersQuery.data],
  );

  const rows = candidates ?? [];
  const draftOf = (candidate: MeetingTaskCandidate): CandidateDraft =>
    drafts[candidate.id] ?? initialDraft(candidate);

  const patchDraft = (candidate: MeetingTaskCandidate, patch: Partial<CandidateDraft>) =>
    setDrafts((prev) => ({
      ...prev,
      [candidate.id]: { ...draftOf(candidate), ...patch },
    }));

  const keptRows = rows.filter((c) => draftOf(c).keep);
  const missingAssignee = keptRows.filter((c) => !draftOf(c).assigneeId);

  const handleConfirm = () => {
    const decisions: MeetingTaskCandidateDecision[] = rows.map((c) => {
      const draft = draftOf(c);
      if (!draft.keep) return { taskId: c.id, keep: false };
      return {
        taskId: c.id,
        keep: true,
        assigneeId: draft.assigneeId ?? null,
        deadlineAt: draft.deadline ? draft.deadline.toISOString() : null,
        priority: draft.priority,
      };
    });
    onConfirm(decisions);
  };

  if (isLoading) {
    return (
      <div data-testid="meeting-candidates-loading">
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        title="暂无待确认的待办"
        description={
          emptyHint ??
          "导入会议内容后点击「生成待办抽取」，识别出的待办会先落在这里，由你逐条确认后进入任务列表。"
        }
      />
    );
  }

  return (
    <div data-testid="meeting-candidates" data-meeting-id={meetingId}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: "var(--jerry-sp-3)" }}
        message="以下待办尚未生效"
        description="这些条目由 AI 从会议内容识别得到，确认之前不会出现在任务列表里。逐条决定是否保留、指派给谁、什么时候截止。"
      />
      {rows.map((candidate) => {
        const draft = draftOf(candidate);
        return (
          <div
            key={candidate.id}
            data-testid="meeting-candidate-row"
            style={{
              border: "1px solid var(--jerry-border-subtle, #E3E6EB)",
              borderRadius: 6,
              padding: "var(--jerry-sp-3)",
              marginBottom: "var(--jerry-sp-3)",
              opacity: draft.keep ? 1 : 0.55,
            }}
          >
            <Checkbox
              checked={draft.keep}
              disabled={submitting}
              onChange={(e) => patchDraft(candidate, { keep: e.target.checked })}
              data-testid={`meeting-candidate-keep-${candidate.id}`}
            >
              <Text strong>{candidate.title}</Text>
            </Checkbox>
            {candidate.notes && (
              <Paragraph
                type="secondary"
                data-testid={`meeting-candidate-notes-${candidate.id}`}
                style={{
                  whiteSpace: "pre-wrap",
                  fontSize: 12,
                  margin: "var(--jerry-sp-2) 0 0 24px",
                  background: "var(--jerry-gray-100)",
                  padding: "8px 10px",
                  borderRadius: 4,
                }}
              >
                {candidate.notes}
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  以上为会议中的原话记录，仅供参考；截止时间以你填写的为准。
                </Text>
              </Paragraph>
            )}
            {draft.keep && (
              <Space wrap style={{ marginTop: "var(--jerry-sp-2)", marginLeft: 24 }}>
                <Select
                  placeholder="指派给"
                  style={{ minWidth: 160 }}
                  options={assigneeOptions}
                  value={draft.assigneeId}
                  disabled={submitting}
                  showSearch
                  optionFilterProp="label"
                  onChange={(value) => patchDraft(candidate, { assigneeId: value })}
                  status={draft.assigneeId ? undefined : "error"}
                  aria-label={`指派给（${candidate.title}）`}
                  data-testid={`meeting-candidate-assignee-${candidate.id}`}
                />
                <DatePicker
                  placeholder="截止时间（可留空）"
                  style={{ minWidth: 180 }}
                  showTime={{ format: "HH:mm" }}
                  format="YYYY-MM-DD HH:mm"
                  value={draft.deadline}
                  disabled={submitting}
                  onChange={(value) => patchDraft(candidate, { deadline: value })}
                  disabledDate={(d) => d && d < dayjs().startOf("day")}
                  aria-label={`截止时间（${candidate.title}）`}
                  data-testid={`meeting-candidate-deadline-${candidate.id}`}
                />
                <Select
                  style={{ minWidth: 100 }}
                  options={PRIORITY_OPTIONS}
                  value={draft.priority}
                  disabled={submitting}
                  onChange={(value) => patchDraft(candidate, { priority: value })}
                  aria-label={`优先级（${candidate.title}）`}
                />
                <Tag color={PRIORITY_TAG_COLOR[draft.priority] ?? "blue"}>
                  {PRIORITY_OPTIONS.find((p) => p.value === draft.priority)?.label ?? "常规"}
                </Tag>
              </Space>
            )}
          </div>
        );
      })}

      <Divider style={{ margin: "var(--jerry-sp-3) 0" }} />

      {submitError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: "var(--jerry-sp-3)" }}
          data-testid="meeting-candidates-error"
          message="确认未生效"
          description={`${submitError}这一批待办没有任何一条生效，修正后可以重新确认。`}
        />
      )}

      <Space wrap>
        {confirmed ? (
          <Button
            type="primary"
            loading={submitting}
            disabled={missingAssignee.length > 0}
            onClick={handleConfirm}
            data-testid="meeting-candidates-confirm"
          >
            确认这 {keptRows.length} 条待办
          </Button>
        ) : (
          <Tooltip title="请先确认纪要">
            <Button type="primary" disabled data-testid="meeting-candidates-confirm">
              确认这 {keptRows.length} 条待办
            </Button>
          </Tooltip>
        )}
        <Text type="secondary">
          保留 {keptRows.length} 条，丢弃 {rows.length - keptRows.length} 条
        </Text>
        {confirmed && missingAssignee.length > 0 && (
          <Text type="danger" data-testid="meeting-candidates-assignee-hint">
            还有 {missingAssignee.length} 条保留的待办没有指派人
          </Text>
        )}
      </Space>
    </div>
  );
}
